import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cn from 'classnames';
import sanitizer from 'sanitizer';
import ExecutionEnvironment from 'exenv';

import { LANDING_PAGE } from 'constants/amethystPageTypes';
import { evFitSurveyResponseSendBeacon, evLandingPageComponentClick, pvHome, pvLanding } from 'events/landing';
import { HYDRA_PERSONALIZED_CATEGORIES, HYRDA_SIXPM_AUTOMATED_PAGES } from 'constants/hydraTests';
import { isAssigned, isInAssignment, triggerAssignment } from 'actions/ab';
import { pageTypeChange } from 'actions/common';
import { firePixelServer } from 'actions/pixelServer';
import { fetchAllRecommenderDataIfNecessary, getIpRestrictedStatus, loadLandingPage } from 'actions/landing/landingPageInfo';
import ErrorResistantLandingSlot from 'containers/ErrorResistantLandingSlot';
import { Loader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { stripSpecialCharsDashReplace } from 'helpers';
import marketplace from 'cfg/marketplace.json';
import { titaniteView, track } from 'apis/amethyst';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { setupLandingEventWatcher, shouldLazyLoad } from 'helpers/LandingPageUtils';
import { getHeartingStyleIdsForComponent, getHeartProps } from 'helpers/HeartUtils';
import { getHeartCounts, getHearts, heartProduct, toggleHeartingLoginModal, unHeartProduct } from 'actions/hearts';
import { checkIsHomepage } from 'history/historyFactory.js';

import css from 'styles/containers/landing.scss';
const {
  shortName,
  homepage,
  hasHearting,
  landing: { hasAutomatedPageRedirects }
} = marketplace;

const HEARTING_AWARE_COMPONENTS = [
  'genericBrandTrending',
  'productSearch', // Only productSearch components with the 'melody', 'melody-curated', 'melody-grid' styles will be considered.
  'recommender'
];

const CLIENT_SIDE_HEARTING_AWARE_COMPONENTS = [
  'recommender'
];

export class Landing extends Component {

  static beforeFetchDataOnServer(store) {
    store.dispatch(triggerAssignment(HYDRA_PERSONALIZED_CATEGORIES));
    store.dispatch(triggerAssignment(HYRDA_SIXPM_AUTOMATED_PAGES));
  }

  static fetchDataOnServer(store, location, { pageName }) {
    return store.dispatch(loadLandingPage(checkIsHomepage(location.pathname) ? homepage : pageName));
  }

  constructor(props) {
    super(props);
    this.onLandingComponentClick = this.onLandingComponentClick.bind(this);
    this.trackPageView = this.trackPageView.bind(this);
    this.makePageHeading = this.makePageHeading.bind(this);
    this.getAllHeartCounts = this.getAllHeartCounts.bind(this);
  }

  componentDidMount() {
    const {
      track,
      location = {},
      landingPage: { pageName: currentPage, pageInfo = {} },
      pageTypeChange,
      fetchAllRecommenderDataIfNecessary,
      params: { pageName: newPage },
      setupLandingEventWatcher,
      isHomepage,
      router,
      getHearts,
      triggerAssignment,
      isCustomer
    } = this.props;

    triggerAssignment(HYDRA_PERSONALIZED_CATEGORIES);
    const hydraSixPMAutomatedPages = isInAssignment(triggerAssignment(HYRDA_SIXPM_AUTOMATED_PAGES));

    if (currentPage === 'mens' && hydraSixPMAutomatedPages && hasAutomatedPageRedirects) {
      router.replace('/c/mens-auto');
      return;
    }

    const { slotData } = pageInfo;

    const { query = {} } = location;
    // Note: When we're on homepage, newPage is undefined
    const isDifferentPage = (!currentPage || (newPage && currentPage !== newPage) || (isHomepage && currentPage !== homepage));

    // Set the correct page type
    const pageType = isHomepage ? 'homepage' : 'landing';
    pageTypeChange(pageType);

    if (isDifferentPage) {
      this.fetchData(isHomepage ? homepage : newPage);
    } else {
      // Track page view right away if we already have the data.
      // Otherwise wait for data first in willReceiveProps
      this.trackPageView(newPage);
    }

    fetchAllRecommenderDataIfNecessary(pageInfo.slotData);

    // Get hearting list
    if (isCustomer) {
      const heartOnLoad = location.query?.heartOnLoad || null;
      getHearts(heartOnLoad);
    }

    this.getAllHeartCounts(slotData);

    if (query.utm_content && query.utm_content.includes('postPurchaseSizing')) {
      track(evFitSurveyResponseSendBeacon, { query });
    }

    setupLandingEventWatcher(this);

    this.getIpRestrictedInfo();
  }

  componentDidUpdate(prevProps) {
    const {
      params: nextPropParams,
      landingPage: nextLandingPage,
      isHomepage,
      recommenderEntries: nextRecommenderEntries,
      hydraSixPMAutomatedPages,
      router
    } = this.props;
    // if going from one landing page to another, update state
    const { params: { pageName: currentPage }, landingPage: { pageInfo }, recommenderEntries } = prevProps;
    const { pageName: newPage } = nextPropParams;
    const { pageInfo: nextPageInfo } = nextLandingPage;

    if (newPage === 'mens' && hydraSixPMAutomatedPages && hasAutomatedPageRedirects) {
      router.replace('/c/mens-auto');
      return;
    }

    if (currentPage !== newPage) {
      if (isHomepage) {
        this.fetchData(homepage);
      } else if (nextLandingPage.pageName !== newPage) {
        // only load this LP if it's not already loaded
        this.fetchData(newPage);
      }
    }

    if (recommenderEntries !== nextRecommenderEntries) {
      const { slotData = {} } = nextPageInfo;
      if (Object.values(slotData).some(slot => CLIENT_SIDE_HEARTING_AWARE_COMPONENTS.includes(slot.componentName))) {
        this.getAllHeartCounts(slotData);
      }
    }

    if (nextLandingPage.pageInfo && pageInfo?.canonicalUrl !== nextLandingPage?.pageInfo?.canonicalUrl) {
      this.trackPageView(newPage, this.props);
      this.getIpRestrictedInfo();
    }
  }

  getIpRestrictedInfo = () => {
    const { landingPage: { ipStatus: { callCompleted } = {}, pageInfo: { ipRestrictedContentPresent = false } = {} }, getIpRestrictedStatus } = this.props;
    if (ipRestrictedContentPresent && !callCompleted && ExecutionEnvironment.canUseDOM) {
      getIpRestrictedStatus();
    }
  };

  getAllHeartCounts(slotData = {}) {
    const { getHeartCounts, recommenderEntries } = this.props;
    const allHeartingStyleIds = [];
    Object.values(slotData).forEach(({ componentName }) => {
      if (HEARTING_AWARE_COMPONENTS.includes(componentName)) {
        allHeartingStyleIds.push(...getHeartingStyleIdsForComponent(slotData, componentName, recommenderEntries));
      }
    });

    if (allHeartingStyleIds.length) {
      getHeartCounts(allHeartingStyleIds);
    }
  }

  fetchData(newPage) {
    const { loadLandingPage } = this.props;
    loadLandingPage(newPage);
  }

  trackPageView(pageName, props = this.props) {
    const {
      firePixelServer,
      trackEvent,
      track,
      isHomepage,
      landingPage: { pageInfo }
    } = props;

    if (isHomepage) {
      trackEvent('TE_PV_HOMEPAGE');
      firePixelServer('home');
    } else {
      trackEvent('TE_PV_LANDINGPAGE');
      firePixelServer('landing', {}, pageName);
    }

    titaniteView();

    const slotData = pageInfo ? pageInfo.slotData : null;
    if (isHomepage && slotData) {
      track(pvHome, { slotData });
    } else if (slotData) {
      track(pvLanding, { pageName, slotData });
    }
  }

  makePageHeading() {
    const { landingPage: { pageInfo: { pageHeading } }, isHomepage } = this.props;
    if (isHomepage) {
      return (
        <h1
          className={css.screenReadersOnly}
          data-test-id={this.context.testId('heading')}>
          {`${shortName} Homepage`}
        </h1>
      );
    } else if (pageHeading) {
      return (
        <h1
          className={css.heading}
          data-test-id={this.context.testId('heading')}
          dangerouslySetInnerHTML={ { __html: sanitizer.sanitize(pageHeading) } }/>
      );
    }
  }

  onLandingComponentClick(e) {
    // Send Analytics component click data via trackLegacyEvent(). The format of this data will change in the future
    // but for now we're sending the landing page parameters the way analytics/siteops wants them, mostly
    // the same as how they're currently formatted in legacy
    const { landingPage, trackLegacyEvent, isHomepage } = this.props;
    const { currentTarget } = e;
    let action = `Landing-${landingPage.pageInfo.subPageType}-${landingPage.pageName}`;
    const label = stripSpecialCharsDashReplace(currentTarget.getAttribute('data-eventlabel'));
    const value = stripSpecialCharsDashReplace(currentTarget.getAttribute('data-eventvalue'));
    const slotIndex = stripSpecialCharsDashReplace(currentTarget.getAttribute('data-slotindex'));

    e.stopPropagation();
    // special labels for homepage per analytics instructions
    if (isHomepage) {
      action = `Gateway-${landingPage.pageName}`;
    }
    trackLegacyEvent(action, label, value);
    track(evLandingPageComponentClick, { label, value, slotIndex });
  }

  render() {
    const {
      landingPage: { isLoaded, pageInfo, pageName, slotOrder },
      isCustomer,
      heartProduct,
      hearts,
      products,
      toggleHeartingLoginModal,
      trackEvent,
      unHeartProduct,
      ipStatus,
      slotContentTypesList
    } = this.props;
    if (!isLoaded) {
      return <Loader />;
    }

    const heartProps = {
      hasHearting,
      isCustomer,
      heartProduct,
      hearts,
      products,
      toggleHeartingLoginModal,
      trackEvent,
      unHeartProduct
    };
    const heartsData = getHeartProps(heartProps, { addedFrom: LANDING_PAGE, heartEventName: 'TE_LANDING_PRODUCT_HEART', unHeartEventName: 'TE_LANDING_PRODUCT_UNHEART' });

    const { slotData, pageLayout } = pageInfo;
    const { testId } = this.context;
    const thisContainer = this;
    return (
      <SiteAwareMetadata loading={!isLoaded}>
        <div
          className={cn(
            css.pageWrap,
            // remove extra spacing if no banner
            { [css.zapposHomepageNoBanner] : pageName === 'zappos-homepage' }
          )}
          data-test-id={testId('landingPage')}
          data-layout={pageLayout} // used for SiteMerch bookmarklets
          data-page-id={pageName}>
          {thisContainer.makePageHeading()}
          {slotOrder.map((slotName, slotIndex) => (
            <ErrorResistantLandingSlot
              key={slotName}
              slotName={slotName}
              slotIndex={slotIndex}
              data={slotData[slotName]}
              onLandingComponentClick={thisContainer.onLandingComponentClick}
              slotHeartsData={HEARTING_AWARE_COMPONENTS.includes(slotData[slotName]?.componentName) && heartsData}
              shouldLazyLoad={shouldLazyLoad(slotIndex)}
              ipStatus={ipStatus}
              slotContentTypesList={slotContentTypesList} />
          ))}
        </div>
      </SiteAwareMetadata>
    );
  }
}

Landing.defaultProps = {
  trackEvent,
  trackLegacyEvent,
  track,
  setupLandingEventWatcher
};

Landing.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

function mapStateToProps(state, ownProps) {
  const isCustomer = !!(ExecutionEnvironment.canUseDOM && state.cookies['x-main']);
  return {
    landingPage: state.landingPage,
    ipStatus: state.landingPage.ipStatus,
    isHomepage: checkIsHomepage(ownProps.location.pathname),
    hearts: state.hearts?.heartsStyleIds,
    recommenderEntries: state.recos,
    isCustomer,
    products: state.products,
    hydraPersonalizedCategories: isAssigned(HYDRA_PERSONALIZED_CATEGORIES, 1, state),
    hydraSixPMAutomatedPages: isAssigned(HYRDA_SIXPM_AUTOMATED_PAGES, 1, state)
  };
}

export default connect(mapStateToProps,
  {
    loadLandingPage,
    getIpRestrictedStatus,
    fetchAllRecommenderDataIfNecessary,
    firePixelServer,
    pageTypeChange,
    getHeartCounts,
    getHearts,
    heartProduct,
    unHeartProduct,
    toggleHeartingLoginModal,
    triggerAssignment
  })(Landing);
